import * as React from "react";
import { useLocation } from "react-router";

import logger from "../../../log";

interface BackNavConfig {
    navigateToOnDeepLink?: string;
}

const BackNavContext = React.createContext<BackNavConfig>({});

interface BackNavProviderProps extends React.PropsWithChildren {
    navigateToOnDeepLink?: string;
}

export function BackNavProvider({
    navigateToOnDeepLink,
    children,
}: BackNavProviderProps): React.JSX.Element {
    const value = React.useMemo(
        () => ({ navigateToOnDeepLink }),
        [navigateToOnDeepLink],
    );
    return (
        <BackNavContext.Provider value={value}>
            {children}
        </BackNavContext.Provider>
    );
}

interface BackNavResult {
    target: string | number;
    state?: Record<string, unknown>;
}

export function useBackNav(): BackNavResult {
    const { navigateToOnDeepLink } = React.useContext(BackNavContext);
    const location = useLocation();

    return React.useMemo(() => {
        // Deep link case: no history present, but we have explicit direction
        if (
            window.history.state &&
            (window.history.state.idx === 0 || location.state?.idx === 0) &&
            navigateToOnDeepLink
        ) {
            logger.info(
                `[useBackNav] - Back navigates to: ${navigateToOnDeepLink}`,
            );
            return {
                target: navigateToOnDeepLink,
                state: { deep: navigateToOnDeepLink },
            };
        }

        // Page directly after a deep link redirect
        if (
            window.history.state &&
            window.history.state.idx === 1 &&
            location.state?.deep === location.pathname
        ) {
            logger.info(`[useBackNav] - Back navigates to "/"`);
            return { target: "/" };
        }

        logger.info(`[useBackNav] - Back navigates back 1.`);
        return { target: -1 };
    }, [location, navigateToOnDeepLink]);
}
